// src/hooks/useToast.ts
import { useState, useCallback } from 'react';
import React from 'react';
import { useErrorContext } from '../errors/ErrorContext';

type ToastType = 'success' | 'error';

interface ToastState {
  show: boolean;
  type: ToastType;
  message: string;
}

interface EventToastState {
  show: boolean;
  position: { x: number; y: number } | null;
  icon?: React.ReactNode;
}

interface UseToastReturn {
  toast: ToastState;
  eventToast: EventToastState;
  showSuccess: (message?: string) => void;
  showError: (message?: string) => void;
  showEventToast: (
    position: { x: number; y: number },
    icon?: React.ReactNode
  ) => void;
  hideToast: () => void;
  hideEventToast: () => void;
}

export const useToast = (): UseToastReturn => {
  // Existing centered toast state
  const [toast, setToast] = useState<ToastState>({
    show: false,
    type: 'success',
    message: '',
  });

  // New event toast state
  const [eventToast, setEventToast] = useState<EventToastState>({
    show: false,
    position: null,
    icon: undefined,
  });

  const errorContext = useErrorContext();

  // Existing toast functions
  const showSuccess = useCallback((message: string = '') => {
    setToast({
      show: true,
      type: 'success',
      message,
    });
  }, []);

  const showError = useCallback((message: string = '') => {
    setToast({
      show: true,
      type: 'error',
      message,
    });
  }, []);

  const hideToast = useCallback(() => {
    setToast((prev) => ({
      ...prev,
      show: false,
    }));
    // Clear global error when toast is dismissed
    errorContext.clearError();
  }, [errorContext]);

  // New event toast functions
  const showEventToast = useCallback(
    (position: { x: number; y: number }, icon?: React.ReactNode) => {
      setEventToast({
        show: true,
        position,
        icon,
      });
    },
    []
  );

  const hideEventToast = useCallback(() => {
    setEventToast({
      show: false,
      position: null,
      icon: undefined,
    });
  }, []);

  return {
    toast,
    eventToast,
    showSuccess,
    showError,
    showEventToast,
    hideToast,
    hideEventToast,
  };
};
